// Two idioms share one handle.
//
// CRUISE is hand flight (`src/sim/flight.ts`): held axes and a held throttle.
//   - right mouse button held   aim the nose. Where the button went down is
//                               centre; the pointer's offset from it is the
//                               held deflection, so holding the mouse off
//                               centre keeps the nose turning.
//   - A / D, Left / Right       yaw the nose left / right
//   - Up / Down                 pitch the nose up / down
//   - W / S                     throttle forward / back
//   - Q / E                     roll left / right
//   - Space                     all-stop, which zeroes the throttle
//
// The keys and the aim drag ADD: neither cancels the other, and a full
// deflection on both is still one full deflection.
//
// The camera is the other half, and never touches the ship: a LEFT drag looks
// around it, the WHEEL (or = and - held) moves in and out along the line to
// it, C brings the view back behind the nose and G resets it there at its
// default distance. `src/cruise/cameraRig.ts` owns the rig; this module only
// reports the pixels and the notches, and hands them over once each.
//
// A FIGHT keeps the SFC / naval idiom, inside the bubble: heading is intent
// (click in 3D), throttle is band selection (click bar). `src/main.ts` branches
// on whether an engagement is mounted, and the axes below reach BOTH branches -
// in a fight they move those same two setpoints rather than the ship, yaw
// slewing the commanded heading and W / S ramping the commanded speed in the
// class's own units. Yaw slews at the hull's pivot rate, which the fight does
// not tax for speed, so A and D answer the same in the bubble as in cruise;
// what going fast costs there is how long the COURSE takes to follow the nose.
// Pitch and roll have nothing to turn in a flat bubble.
//
// Which key does what is src/keyBindings.ts: the held axes are `HELD_KEYS`,
// the discrete press events `PRESS_KEYS`, and the key list draws both.

import { isMode, isModalOpen, onModeChange } from './appMode';
import { tabulaKey } from './tabula';
import { HELD_KEYS, pressKeyFor, type HeldKey, type PressKey } from './keyBindings';
import { TEST_SWITCHES } from './urlFlags.ts';

/** Pointer offset in px for a full deflection, and the dead zone inside it. */
const AIM_RADIUS_PX = 200;
const AIM_DEADZONE_PX = 8;
/** A left-drag past this many px is a look-around, so the world click it would
 *  otherwise end with (lock a target, set a heading) is dropped. */
const ORBIT_CLICK_PX = 4;
/** Wheel notches a second while = (in) or - (out) is held. */
const ZOOM_KEY_NOTCHES_PER_S = 5;
/** A take after a longer gap than this counts only this much held zoom. */
const ZOOM_KEY_MAX_DT_S = 0.1;

function clamp1(v: number): number {
  return v < -1 ? -1 : v > 1 ? 1 : v;
}

export interface InputState {
  allStop: boolean; // Space held
  /** Nose up, [-1, 1]. Up / Down arrows and the right-button drag, added. */
  pitch: number;
  /** Nose left, [-1, 1]. A / D, Left / Right and the right-button drag, added. */
  yaw: number;
  /** Counter-clockwise seen from behind, [-1, 1]. Q / E. */
  roll: number;
  /** Along the nose, [-1, 1]. W / S, forced to 0 by all-stop. */
  throttle: number;
  /** True while W or S is actually held, so a caller can tell "0" from "released". */
  throttleKey: boolean;
  /** The last left-button release ended a look-around, so it was not a click. */
  suppressClick: boolean;
}

export type InputEvent =
  | 'het'
  | 'land'
  | 'launch'
  | 'cycleNext'
  | 'cyclePrev'
  | 'redAlert'
  | 'radiators'
  | 'redLine'
  | 'clearTarget'
  | 'fire'
  | 'firePhaser'
  | 'firePulse'
  | 'fireLance'
  | 'simSpeedDown'
  | 'simSpeedUp'
  | 'chaseView'
  | 'cameraReset'
  | 'pin'
  | 'tabula'
  | 'names'
  | 'dvMap'
  | 'warpLevel1'
  | 'warpLevel2'
  | 'warpLevel3'
  | 'warpLevel4'
  | 'keys';

export interface InputHandle {
  state: InputState;
  on: (event: InputEvent, handler: () => void) => void;
  /** Left-drag pixels since the last call, and zero after it. */
  takeOrbit: () => { dx: number; dy: number };
  /** Wheel notches since the last call, with = and - held counted as notches
   *  at `ZOOM_KEY_NOTCHES_PER_S`, and zero after it. Positive is out. */
  takeWheel: () => number;
  dispose: () => void;
}

/**
 * The control (`?hudwheelblind=1`): the camera takes every wheel again,
 * whatever it is over, which is what it did before the guard below. A panel's
 * scroll box must stop moving under the wheel and the camera must start
 * zooming on it. Nothing a player reaches sets it.
 */
const WHEEL_BLIND = TEST_SWITCHES && new URLSearchParams(location.search).get('hudwheelblind') === '1';

/**
 * The control (`?dvtablekeysleak=1`): put the pre-fix behaviour back - a key
 * already held when the delta-v map's wide window (`dvtable` mode) opens
 * keeps acting for as long as the window stays open, since only a fresh
 * keydown was ever blocked (src/hudDvTable.ts's capture listener). The
 * held-key leg in tools/preview/dvmap-panel-fit.mjs has to fail on it.
 */
const DVTABLE_KEYS_LEAK = TEST_SWITCHES
  && new URLSearchParams(location.search).get('dvtablekeysleak') === '1';

export function createInput(): InputHandle {
  const state: InputState = {
    allStop: false, pitch: 0, yaw: 0, roll: 0, throttle: 0, throttleKey: false,
    suppressClick: false,
  };
  const keys = new Set<string>();
  // Where the right button went down, and the live pointer, in client px.
  let aiming = false;
  let aimX = 0;
  let aimY = 0;
  let pointerX = 0;
  let pointerY = 0;
  // The camera's own drag, on the other button, and the wheel. Both are handed
  // to the caller once and cleared, so a frame that never ran cannot replay one.
  let orbiting = false;
  let orbitLastX = 0;
  let orbitLastY = 0;
  let orbitMovedPx = 0;
  let orbitDx = 0;
  let orbitDy = 0;
  let wheelNotches = 0;
  /** Held zoom, -1 in to +1 out, and the wall clock of the last take. */
  let zoomHeld = 0;
  let zoomTakenAtMs = performance.now();
  const handlers = new Map<InputEvent, Array<() => void>>();

  function on(event: InputEvent, handler: () => void): void {
    let arr = handlers.get(event);
    if (!arr) {
      arr = [];
      handlers.set(event, arr);
    }
    arr.push(handler);
  }

  function emit(event: InputEvent): void {
    handlers.get(event)?.forEach((h) => h());
  }

  function fire(press: PressKey, e: KeyboardEvent): void {
    // A true modal (src/buildingDetail.ts) swallows the digits itself; this is the
    // guard behind that, since 1-4 leaking behind the lightbox is what review caught.
    if (press.guard === 'noModal' && isModalOpen()) return;
    if (press.guard === 'tabula' && !tabulaKey(e, isMode)) return;
    for (const ev of press.events) emit(ev);
  }

  /** The held keys on `axis`, summed by sign. */
  function held(axis: HeldKey['axis']): number {
    let sum = 0;
    for (const h of HELD_KEYS) if (h.axis === axis && keys.has(h.key)) sum += h.sign;
    return sum;
  }

  /** The pointer's offset from the aim centre, as a deflection in [-1, 1]. */
  function deflect(d: number): number {
    const mag = Math.abs(d);
    if (mag <= AIM_DEADZONE_PX) return 0;
    const t = (mag - AIM_DEADZONE_PX) / (AIM_RADIUS_PX - AIM_DEADZONE_PX);
    return Math.sign(d) * Math.min(1, t);
  }

  function recompute(): void {
    // The delta-v map's wide window (`dvtable` mode) takes every keydown
    // before it reaches `onDown` below, but a key already held when the
    // window opens never gets a fresh event to filter: it just sits in
    // `keys`. Reading held keys as released while the mode is on, rather
    // than clearing `keys` itself, means a keyup during the block (still
    // delivered - only keydown is captured away) is never lost, and closing
    // the window resumes exactly whatever is still truly held.
    const blocked = isMode('dvtable') && !DVTABLE_KEYS_LEAK;
    state.allStop = !blocked && held('allStop') > 0;
    state.roll = blocked ? 0 : held('roll');
    state.throttleKey = !blocked && HELD_KEYS.some((h) => h.axis === 'throttle' && keys.has(h.key));
    const throttle = blocked ? 0 : held('throttle');
    state.throttle = state.allStop ? 0 : throttle;
    // Positive yaw is nose LEFT and positive pitch nose UP, the senses
    // `src/sim/flight.ts` takes its body axes in.
    const keyYaw = blocked ? 0 : held('yaw');
    const keyPitch = blocked ? 0 : held('pitch');
    // Screen y grows downward and a nose-right turn is a negative yaw about
    // the ship's own +Y, so both drag axes come back negated.
    const dragYaw = aiming ? -deflect(pointerX - aimX) : 0;
    const dragPitch = aiming ? -deflect(pointerY - aimY) : 0;
    state.yaw = clamp1(keyYaw + dragYaw);
    state.pitch = clamp1(keyPitch + dragPitch);
    zoomHeld = blocked || cameraBlocked() ? 0
      : (keys.has('-') || keys.has('_') ? 1 : 0) - (keys.has('=') || keys.has('+') ? 1 : 0);
  }

  function isEditableFocused(): boolean {
    const a = document.activeElement;
    if (!a) return false;
    const tag = a.tagName;
    return tag === 'INPUT' || tag === 'TEXTAREA' || (a as HTMLElement).isContentEditable === true;
  }

  function isEditorMode(): boolean {
    return isMode('editor');
  }

  /** The Atlas has the frame (src/main.ts's land verb); its own keys own it. */
  function isLandMode(): boolean {
    return isMode('land');
  }

  /** The menu pane is up, and the Atlas mounted inside it owns the wheel. */
  function isMenuMode(): boolean {
    return isMode('menu');
  }

  /** Nothing of the camera's is the cruise rig's while a stage has the frame. */
  function cameraBlocked(): boolean {
    return isEditableFocused() || isEditorMode() || isLandMode() || isMenuMode() || isMode('dvtable');
  }

  /** The HUD scroll box the pointer is in, when it has something left to
   *  scroll. A `[data-hud-scroll]` box that is not scrolling right now (the
   *  comms log is a plain clipped box until it is expanded) or that is already
   *  showing all it has leaves the wheel to the camera. */
  function hudScrollUnder(target: EventTarget | null): HTMLElement | null {
    if (WHEEL_BLIND) return null;
    if (!(target instanceof Element)) return null;
    const box = target.closest<HTMLElement>('[data-hud-scroll]');
    if (box === null) return null;
    const overflow = getComputedStyle(box).overflowY;
    if (overflow !== 'auto' && overflow !== 'scroll') return null;
    return box.scrollHeight - box.clientHeight > 1 ? box : null;
  }

  function onDown(e: KeyboardEvent): void {
    // Don't capture ship controls while the player is typing in an input
    // or while the Game Editor is open
    if (isEditableFocused() || isEditorMode()) return;
    const k = e.key.toLowerCase();
    // The Atlas has the frame and its own keys own it, with one exception: a
    // ship that landed hands the view to the ladder, and LAUNCH is what takes
    // the hull off that ground. `src/main.ts` decides whether it does anything.
    const press = pressKeyFor(k, e.shiftKey);
    if (isLandMode()) {
      if (press?.inAtlas && !e.repeat) fire(press, e);
      return;
    }
    // Space scrolls the page and the arrows scroll it and move the caret.
    if (k === ' ' || k.startsWith('arrow')) e.preventDefault();

    // Edge-trigger discrete events (only on first press, not repeat)
    if (!e.repeat && press) fire(press, e);

    keys.add(k);
    recompute();
  }

  function onUp(e: KeyboardEvent): void {
    // Always release on keyup (in case the player started holding outside the input
    // and then focused it). Safe to clear regardless of focus state.
    keys.delete(e.key.toLowerCase());
    recompute();
  }

  function onBlur(): void {
    keys.clear();
    aiming = false;
    orbiting = false;
    recompute();
  }

  function onMouseDown(e: MouseEvent): void {
    if (e.button === 0) {
      // Not prevented: a left press that never moves is still the world click
      // that locks a target or sets a heading.
      if (cameraBlocked()) return;
      // Only a press on the drawn scene. A drag that starts on the throttle
      // bar, a slider or a panel belongs to that control, not to the camera.
      if ((e.target as HTMLElement | null)?.tagName !== 'CANVAS') return;
      orbiting = true;
      orbitLastX = e.clientX;
      orbitLastY = e.clientY;
      orbitMovedPx = 0;
      state.suppressClick = false;
      return;
    }
    if (e.button !== 2) return;
    if (isEditableFocused() || isEditorMode() || isLandMode() || isMode('dvtable')) return;
    e.preventDefault();
    aiming = true;
    aimX = e.clientX;
    aimY = e.clientY;
    pointerX = e.clientX;
    pointerY = e.clientY;
    recompute();
  }

  function onMouseMove(e: MouseEvent): void {
    if (orbiting) {
      const dx = e.clientX - orbitLastX;
      const dy = e.clientY - orbitLastY;
      orbitLastX = e.clientX;
      orbitLastY = e.clientY;
      orbitMovedPx += Math.abs(dx) + Math.abs(dy);
      orbitDx += dx;
      orbitDy += dy;
    }
    if (!aiming) return;
    pointerX = e.clientX;
    pointerY = e.clientY;
    recompute();
  }

  function onMouseUp(e: MouseEvent): void {
    if (e.button === 0) {
      if (!orbiting) return;
      orbiting = false;
      state.suppressClick = orbitMovedPx > ORBIT_CLICK_PX;
      return;
    }
    if (e.button !== 2 || !aiming) return;
    aiming = false;
    recompute();
  }

  /** In and out along the camera's own line to the ship. */
  function onWheel(e: WheelEvent): void {
    // A wheel inside a HUD panel's own scroll box belongs to that panel. This
    // listener is on `window`, so before the guard every notch over the
    // delta-v map's list and over the expanded comms log zoomed the camera,
    // and the `preventDefault` below took the panel's own scrolling away with
    // it - the scrollbar could only be dragged by hand.
    if (hudScrollUnder(e.target) !== null) return;
    if (cameraBlocked()) return;
    e.preventDefault();
    // Lines rather than pixels on Firefox and on some mice; the Atlas's own
    // wheel (src/atlas.ts) reads it the same way, so a notch is one notch in
    // both places.
    const pixels = e.deltaMode === 1 ? e.deltaY * 16 : e.deltaY;
    wheelNotches += Math.max(-2, Math.min(2, pixels / 100));
  }

  /** The browser menu would eat the drag; suppress it only while aiming. */
  function onContextMenu(e: MouseEvent): void {
    if (aiming) e.preventDefault();
  }

  window.addEventListener('keydown', onDown);
  window.addEventListener('keyup', onUp);
  window.addEventListener('blur', onBlur);
  window.addEventListener('mousedown', onMouseDown);
  window.addEventListener('mousemove', onMouseMove);
  window.addEventListener('mouseup', onMouseUp);
  window.addEventListener('contextmenu', onContextMenu);
  window.addEventListener('wheel', onWheel, { passive: false });
  // Opening or closing the window is itself the edge: a held key must stop
  // acting the instant it opens and resume (or not) the instant it shuts,
  // not wait for the next keyboard event to call recompute() again.
  const offDvTableMode = onModeChange((mode) => {
    if (mode === 'dvtable') recompute();
  });

  function dispose(): void {
    window.removeEventListener('keydown', onDown);
    window.removeEventListener('keyup', onUp);
    window.removeEventListener('blur', onBlur);
    window.removeEventListener('mousedown', onMouseDown);
    window.removeEventListener('mousemove', onMouseMove);
    window.removeEventListener('mouseup', onMouseUp);
    window.removeEventListener('contextmenu', onContextMenu);
    window.removeEventListener('wheel', onWheel);
    offDvTableMode();
  }

  function takeOrbit(): { dx: number; dy: number } {
    const out = { dx: orbitDx, dy: orbitDy };
    orbitDx = 0;
    orbitDy = 0;
    return out;
  }

  function takeWheel(): number {
    const now = performance.now();
    const dt = Math.min(Math.max(0, (now - zoomTakenAtMs) / 1000), ZOOM_KEY_MAX_DT_S);
    zoomTakenAtMs = now;
    const out = wheelNotches + zoomHeld * ZOOM_KEY_NOTCHES_PER_S * dt;
    wheelNotches = 0;
    return out;
  }

  return { state, on, takeOrbit, takeWheel, dispose };
}
